import React, { useState, useEffect, useCallback, useRef } from 'react';
import { OrderService } from '../../services/OrderService';
import { useStore } from '../../contexts/StoreContext';

interface Props {
    cartaoCode?: string;
    comandaId?: string;
    senha?: string;
}

const POLL_MS = 15000;

export const ComandaViewer: React.FC<Props> = ({ cartaoCode, comandaId, senha }) => {
    const { store } = useStore();
    const [comanda, setComanda] = useState<any>(null);
    const [expanded, setExpanded] = useState(false);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchComanda = useCallback(async () => {
        try {
            if (comandaId && senha) {
                const res = await OrderService.getComandaByIdClient(comandaId, senha);
                if (res?.comanda) setComanda(res.comanda);
            } else if (cartaoCode && store) {
                const res = await OrderService.getComandaByCartao(store.id, cartaoCode);
                if (res.success) setComanda(res.comanda);
            }
        } catch {}
    }, [store, cartaoCode, comandaId, senha]);

    useEffect(() => {
        fetchComanda();
        pollingRef.current = setInterval(fetchComanda, POLL_MS);
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [fetchComanda]);

    if (!comanda) return null;

    const allItems = (comanda.orders || []).flatMap((o: any) => o.items || []);
    const total: number = comanda.total ?? 0;
    const paidAmount: number = comanda.paidAmount ?? 0;
    const aPagar = Math.max(0, total - paidAmount);

    return (
        <div style={{
            margin: '0 16px 12px',
            background: 'white',
            borderRadius: 14,
            border: '2px solid #ea580c',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(234,88,12,0.12)',
        }}>
            {/* Header — sempre visível */}
            <div
                role="button"
                onClick={() => setExpanded(v => !v)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b' }}>
                        Minha Comanda
                    </span>
                    {comanda.mesaNumber && (
                        <span style={{
                            background: '#fff7ed', color: '#ea580c',
                            fontSize: '0.72rem', fontWeight: 800,
                            padding: '2px 8px', borderRadius: 20,
                        }}>
                            Mesa {comanda.mesaNumber}
                        </span>
                    )}
                    {allItems.length > 0 && (
                        <span style={{
                            background: '#f1f5f9', color: '#64748b',
                            fontSize: '0.72rem', fontWeight: 700,
                            padding: '2px 8px', borderRadius: 20,
                        }}>
                            {allItems.length} {allItems.length === 1 ? 'item' : 'itens'}
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 900, color: '#ea580c', fontSize: '0.95rem' }}>
                        R$ {aPagar.toFixed(2)}
                    </span>
                    <span style={{
                        color: '#94a3b8',
                        fontSize: 16,
                        display: 'inline-block',
                        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s',
                        lineHeight: 1,
                    }}>
                        ▾
                    </span>
                </div>
            </div>

            {/* Conteúdo expandido */}
            <div style={{
                display: 'grid',
                gridTemplateRows: expanded ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.25s ease',
            }}>
                <div style={{ overflow: 'hidden', minHeight: 0 }}>
                    <div style={{ borderTop: '1px solid #f1f5f9' }}>
                        {allItems.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '0.85rem' }}>
                                Nenhum item consumido ainda.
                            </div>
                        ) : (
                            <div>
                                <div style={{ maxHeight: 260, overflowY: 'auto', padding: '4px 16px 8px' }}>
                                    {allItems.map((item: any, idx: number) => {
                                        const opts: string[] = [];
                                        if (item.size) opts.push(`Tam: ${item.size}`);
                                        if (item.sideDish) opts.push(`Acomp.: ${item.sideDish}`);
                                        if (item.sideDishes?.length) opts.push(`Acomp.: ${item.sideDishes.join(', ')}`);
                                        if (item.flavors?.length) opts.push(`Sabores: ${item.flavors.join(', ')}`);
                                        if (item.sauces?.length) opts.push(`Molhos: ${item.sauces.map((s: string) => { const q = item.sauceQtys?.[s]; return q && q > 1 ? `${s} x${q}` : s; }).join(', ')}`);
                                        if (item.additionals?.length) {
                                            const names = item.additionals.map((a: any) =>
                                                typeof a === 'object' ? `${a.name}${a.qty > 1 ? ` x${a.qty}` : ''}` : a
                                            );
                                            opts.push(`Adicionais: ${names.join(', ')}`);
                                        }
                                        if (item.removedIngredients?.length) opts.push(`Retirar: ${item.removedIngredients.join(', ')}`);
                                        if (item.observations) opts.push(`Obs: ${item.observations}`);

                                        return (
                                            <div
                                                key={idx}
                                                style={{
                                                    padding: '8px 0',
                                                    borderBottom: idx < allItems.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>
                                                        {item.qty}x {item.name}
                                                    </div>
                                                    <div style={{
                                                        fontWeight: 800, color: '#ea580c',
                                                        fontSize: '0.88rem', flexShrink: 0, marginLeft: 12,
                                                    }}>
                                                        R$ {(item.price * item.qty).toFixed(2)}
                                                    </div>
                                                </div>
                                                {opts.map((opt, i) => (
                                                    <div key={i} style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                                                        {opt}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Totais */}
                                <div style={{ borderTop: '2px dashed #e2e8f0', margin: '0 16px', padding: '10px 0 14px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                                        <span>Total consumido</span>
                                        <span>R$ {total.toFixed(2)}</span>
                                    </div>
                                    {paidAmount > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.82rem', color: '#22c55e', fontWeight: 700 }}>
                                            <span>Pago</span>
                                            <span>R$ {paidAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex', justifyContent: 'space-between',
                                        fontSize: '1rem', fontWeight: 900,
                                        color: aPagar > 0 ? '#ef4444' : '#22c55e',
                                    }}>
                                        <span>{aPagar > 0 ? 'A pagar' : 'Quitado'}</span>
                                        <span>R$ {aPagar.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
